// src/utils/env.ts

interface ImportMetaEnv {
  VITE_BETA_API_KEY: string;
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_SUPABASE_SERVICE_ROLE_KEY: string;
  VITE_API_URL: string;
  // Cloudflare KV Cache Configuration
  VITE_CLOUDFLARE_KV_NAMESPACE_ID: string;
  VITE_CLOUDFLARE_ACCOUNT_ID: string;
  VITE_CLOUDFLARE_API_TOKEN: string;
  VITE_CACHE_DEFAULT_TTL: string;
  VITE_CACHE_MAX_SIZE: string;
  DEV: boolean;
  MODE: string;
  PROD: boolean;
  SSR: boolean;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

// Safe environment variable access with fallbacks
const safeGetEnv = () => {
  try {
    // Check if import.meta.env exists and has expected properties
    if (typeof import.meta !== 'undefined' && 
        import.meta && 
        typeof import.meta.env !== 'undefined' &&
        import.meta.env) {
      
      // Create a safe copy with defaults
      const envWithDefaults = {
        // Use actual env values if available, with safe empty fallbacks
        VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || '',
        VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        VITE_SUPABASE_SERVICE_ROLE_KEY: import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY || '',
        VITE_BETA_API_KEY: import.meta.env.VITE_BETA_API_KEY || '',
        VITE_API_URL: import.meta.env.VITE_API_URL || '',
        
        // Cloudflare KV Cache Configuration
        VITE_CLOUDFLARE_KV_NAMESPACE_ID: import.meta.env.VITE_CLOUDFLARE_KV_NAMESPACE_ID || '',
        VITE_CLOUDFLARE_ACCOUNT_ID: import.meta.env.VITE_CLOUDFLARE_ACCOUNT_ID || '',
        VITE_CLOUDFLARE_API_TOKEN: import.meta.env.VITE_CLOUDFLARE_API_TOKEN || '',
        VITE_CACHE_DEFAULT_TTL: import.meta.env.VITE_CACHE_DEFAULT_TTL || '300000', // 5 minutes default
        VITE_CACHE_MAX_SIZE: import.meta.env.VITE_CACHE_MAX_SIZE || '100', // 100 items default
        
        // Environment flags
        MODE: import.meta.env.MODE || 'production',
        DEV: import.meta.env.DEV || false,
        PROD: import.meta.env.PROD || true,
        SSR: import.meta.env.SSR || false,
      };

      return envWithDefaults;
    }
    
    // Fallback with empty strings for sensitive values
    console.warn('import.meta.env not available, using empty fallback values');
    return {
      VITE_SUPABASE_URL: '',
      VITE_SUPABASE_ANON_KEY: '',
      VITE_SUPABASE_SERVICE_ROLE_KEY: '',
      VITE_BETA_API_KEY: '',
      VITE_API_URL: '',
      VITE_CLOUDFLARE_KV_NAMESPACE_ID: '',
      VITE_CLOUDFLARE_ACCOUNT_ID: '',
      VITE_CLOUDFLARE_API_TOKEN: '',
      VITE_CACHE_DEFAULT_TTL: '300000',
      VITE_CACHE_MAX_SIZE: '100',
      MODE: 'production',
      DEV: false,
      PROD: true,
      SSR: false,
    };
  } catch (error) {
    console.error('Error accessing environment variables:', error);
    return {
      VITE_SUPABASE_URL: '',
      VITE_SUPABASE_ANON_KEY: '',
      VITE_SUPABASE_SERVICE_ROLE_KEY: '',
      VITE_BETA_API_KEY: '',
      VITE_API_URL: '',
      VITE_CLOUDFLARE_KV_NAMESPACE_ID: '',
      VITE_CLOUDFLARE_ACCOUNT_ID: '',
      VITE_CLOUDFLARE_API_TOKEN: '',
      VITE_CACHE_DEFAULT_TTL: '300000',
      VITE_CACHE_MAX_SIZE: '100',
      MODE: 'production',
      DEV: false,
      PROD: true,
      SSR: false,
    };
  }
};

export const env = safeGetEnv();